<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2 class="d-flex align-center">Panel de precios <HelpHint text="Monitorea evolución de listas, variaciones y trazabilidad de cambios con foco comercial y contable." /></h2>
        <p>Vista gerencial de trazabilidad y evolución de listas de precios.</p>
      </div>
      <v-btn color="primary" :loading="loading" @click="refreshAll">Actualizar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Filtros</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.priceListId"
              :items="priceListOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Lista"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.providerId"
              :items="providerOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Proveedor"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.dateFrom" type="date" label="Desde" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="filters.dateTo" type="date" label="Hasta" outlined dense />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-end">
            <v-btn color="primary" block :loading="loading" @click="refreshAll">Aplicar</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Subir lista de precios (Excel/CSV)</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.priceListId"
              :items="priceListOptions"
              item-text="label"
              item-value="id"
              label="Lista destino"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-file-input
              v-model="uploadFile"
              @change="uploadResult = null"
              label="Archivo .xlsx o .csv"
              outlined
              dense
              accept=".xlsx,.xls,.csv"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="uploadDescription" label="Descripción actualización" outlined dense />
          </v-col>
        </v-row>
        <div class="actions-row">
          <v-btn color="primary" :loading="uploadLoading" :disabled="!canUpload" @click="runUpload(true)">Previsualizar</v-btn>
          <v-btn color="success" :loading="uploadLoading" :disabled="!canApplyPriceUpload" @click="runUpload(false)">Confirmar y aplicar</v-btn>
        </div>
        <div v-if="uploadResult" class="mt-3 caption">
          Filas: {{ uploadResult.totalRows }} | Procesadas: {{ uploadResult.processed }} | Creadas: {{ uploadResult.created }} | Actualizadas: {{ uploadResult.updated }} | Omitidas: {{ uploadResult.skipped }}
        </div>
        <v-data-table
          v-if="uploadResult && Array.isArray(uploadResult.errors) && uploadResult.errors.length"
          class="mt-2"
          :headers="uploadErrorHeaders"
          :items="uploadResult.errors"
          dense
          hide-default-footer
          disable-pagination
        />
      </v-card-text>
    </v-card>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Importar coeficientes por artículo</v-card-title>
      <v-card-text>
        <p class="caption mb-3">Archivo Excel/CSV con las columnas <code>Código</code> y <code>Coeficiente</code>. El coeficiente es multiplicador: <code>1,2500</code> equivale a 25% sobre costo. No modifica precios hasta ejecutar luego el cálculo de la lista.</p>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-file-input
              v-model="coefficientUploadFile"
              @change="coefficientUploadResult = null"
              label="Archivo .xlsx o .csv"
              outlined
              dense
              accept=".xlsx,.xls,.csv"
            />
          </v-col>
        </v-row>
        <div class="actions-row">
          <v-btn color="primary" :loading="coefficientUploadLoading" :disabled="!coefficientUploadFile" @click="runCoefficientUpload(true)">Previsualizar</v-btn>
          <v-btn color="success" :loading="coefficientUploadLoading" :disabled="!canApplyCoefficientUpload" @click="runCoefficientUpload(false)">Confirmar y aplicar</v-btn>
        </div>
        <div v-if="coefficientUploadResult" class="mt-3 caption">
          Filas: {{ coefficientUploadResult.totalRows }} | Actualizadas: {{ coefficientUploadResult.updated || 0 }} | Sin cambios: {{ coefficientUploadResult.unchanged || 0 }}
        </div>
        <v-data-table
          v-if="coefficientUploadResult && Array.isArray(coefficientUploadResult.previewRows)"
          class="mt-2"
          :headers="coefficientPreviewHeaders"
          :items="coefficientUploadResult.previewRows"
          dense
          :items-per-page="20"
        />
      </v-card-text>
    </v-card>

    <v-row dense class="mb-2">
      <v-col cols="12" md="3">
        <v-card outlined class="kpi-card">
          <div class="kpi-label">Actualizaciones</div>
          <div class="kpi-value">{{ overview.totals.updates }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined class="kpi-card">
          <div class="kpi-label">Cambios de precio</div>
          <div class="kpi-value">{{ overview.totals.changes }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined class="kpi-card">
          <div class="kpi-label">Artículos afectados</div>
          <div class="kpi-value">{{ overview.totals.changedProducts }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined class="kpi-card">
          <div class="kpi-label">Variación promedio %</div>
          <div class="kpi-value">{{ formatSignedPct(overview.totals.avgDeltaPct) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mb-3">
      <v-col cols="12" md="4">
        <v-card outlined class="insight-card">
          <div class="insight-title">Subidas vs Bajas</div>
          <div class="donut-wrap mb-2">
            <div class="donut" :style="changesDonutStyle"></div>
            <div class="donut-legend">
              <div class="legend-item"><span class="legend-dot legend-dot--up"></span>Subidas {{ changesUpPct }}%</div>
              <div class="legend-item"><span class="legend-dot legend-dot--down"></span>Bajas {{ changesDownPct }}%</div>
            </div>
          </div>
          <div class="insight-row">
            <span class="pill pill--success">Subidas: {{ overview.totals.increases || 0 }}</span>
            <span class="pill pill--warn">Bajas: {{ overview.totals.decreases || 0 }}</span>
          </div>
          <div class="caption mt-2">Balance: {{ balanceLabel }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="insight-card">
          <div class="insight-title">Lectura rápida</div>
          <div class="caption">{{ quickSummaryLine1 }}</div>
          <div class="caption">{{ quickSummaryLine2 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="insight-card">
          <div class="insight-title">Proveedor con más impacto</div>
          <div v-if="providerRows.length" class="donut-wrap mb-2">
            <div class="donut" :style="providerShareDonutStyle"></div>
            <div class="donut-legend">
              <div v-for="item in providerTopSlices" :key="item.providerId" class="legend-item">
                <span class="legend-dot" :style="{ background: item.color }"></span>{{ item.providerCode }} {{ item.sharePct }}%
              </div>
            </div>
          </div>
          <div v-if="providerRows.length" class="caption">
            {{ providerRows[0].providerCode }} - {{ providerRows[0].providerName }}
          </div>
          <div v-if="providerRows.length" class="caption">
            {{ providerRows[0].changes }} cambios ({{ providerRows[0].sharePct }}%)
          </div>
          <div v-else class="caption">Sin datos en el rango seleccionado.</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="7">
        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Tendencia de cambios</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="chart-title">Cantidad de cambios por período</div>
                <v-sparkline
                  :value="trendChangesSeries"
                  color="#0ea5e9"
                  line-width="3"
                  auto-draw
                  padding="8"
                  smooth
                />
                <div class="chart-meta">
                  Último período: {{ lastTrendChanges }} cambios
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-title">Variación promedio % por período</div>
                <v-sparkline
                  :value="trendAvgPctSeries"
                  :color="trendAvgPctColor"
                  line-width="3"
                  auto-draw
                  padding="8"
                  smooth
                />
                <div class="chart-meta">
                  Último período: {{ formatSignedPct(lastTrendAvgPct) }}
                </div>
              </v-col>
            </v-row>
            <div v-for="row in trendRows" :key="row.bucket" class="trend-row mt-2">
              <div class="trend-head">
                <span>{{ row.bucket }}</span>
                <span>{{ row.changes }} cambios · {{ formatSignedPct(row.avgDeltaPct) }}</span>
              </div>
              <div class="trend-track">
                <div
                  class="trend-fill"
                  :style="{ width: `${Math.max(2, Math.round((Number(row.changes || 0) * 100) / maxTrendChanges))}%` }"
                ></div>
              </div>
            </div>
            <v-data-table :headers="trendHeaders" :items="trendRows" dense disable-pagination hide-default-footer>
              <template v-slot:item.avgDeltaPct="{ item }">{{ formatSignedPct(item.avgDeltaPct) }}</template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card outlined class="page-card">
          <v-card-title class="subtitle-1">Top cambios por artículo</v-card-title>
          <v-card-text>
            <v-data-table :headers="topHeaders" :items="topRows" dense>
              <template v-slot:item.previousPrice="{ item }">{{ formatNumber(item.previousPrice) }}</template>
              <template v-slot:item.newPrice="{ item }">{{ formatNumber(item.newPrice) }}</template>
              <template v-slot:item.deltaPct="{ item }">{{ formatSignedPct(item.deltaPct) }}</template>
              <template v-slot:item.changedAt="{ item }">{{ formatDateTime(item.changedAt) }}</template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5">
        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Cuentas corrientes: lectura rápida</v-card-title>
          <v-card-text>
            <div class="donut-wrap mb-3">
              <div class="donut" :style="ccDonutStyle"></div>
              <div class="donut-legend">
                <div class="legend-item"><span class="legend-dot" style="background:#dc2626"></span>Con deuda {{ ccDebtPct }}%</div>
                <div class="legend-item"><span class="legend-dot" style="background:#16a34a"></span>A favor {{ ccCreditPct }}%</div>
                <div class="legend-item"><span class="legend-dot" style="background:#94a3b8"></span>Sin saldo {{ ccZeroPct }}%</div>
              </div>
            </div>
            <div class="insight-row mb-2">
              <span class="pill pill--warn">Deudores: {{ ccDebtCount }}</span>
              <span class="pill pill--success">A favor: {{ ccCreditCount }}</span>
              <span class="pill">Sin saldo: {{ ccZeroCount }}</span>
            </div>
            <div class="caption">
              Saldo total pendiente: {{ formatNumber(ccTotalDebt) }} | Saldo total a favor: {{ formatNumber(ccTotalCredit) }}
            </div>
          </v-card-text>
        </v-card>

        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Últimos movimientos (cuentas corrientes)</v-card-title>
          <v-card-text>
            <v-data-table :headers="ccMovementHeaders" :items="ccRecentMovements" dense disable-pagination hide-default-footer>
              <template v-slot:item.account="{ item }">{{ item.accountCode }} - {{ item.accountName }}</template>
              <template v-slot:item.movementType="{ item }">
                <span :class="['pill', item.movementType === 'debit' ? 'pill--warn' : 'pill--success']">
                  {{ item.movementType === "debit" ? "Débito" : "Crédito" }}
                </span>
              </template>
              <template v-slot:item.amount="{ item }">{{ formatNumber(item.amount) }}</template>
              <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
            </v-data-table>
            <div v-if="!ccRecentMovements.length" class="caption">Todavía no hay movimientos registrados.</div>
          </v-card-text>
        </v-card>

        <v-card outlined class="page-card mb-4">
          <v-card-title class="subtitle-1">Distribución por proveedor</v-card-title>
          <v-card-text>
            <div v-for="row in providerRows" :key="row.providerId" class="provider-share-row">
              <div class="provider-share-head">
                <span>{{ row.providerCode }} - {{ row.providerName }}</span>
                <span>{{ row.sharePct }}%</span>
              </div>
              <div class="provider-share-track">
                <div class="provider-share-fill" :style="{ width: `${Math.max(1, Math.min(100, row.sharePct))}%` }"></div>
              </div>
              <div class="caption">Cambios: {{ row.changes }} | Artículos: {{ row.changedProducts }} | Var %: {{ formatNumber(row.avgDeltaPct) }}</div>
            </div>
          </v-card-text>
        </v-card>

        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header>Auditoría técnica (opcional)</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card outlined class="page-card">
                <v-card-text>
                  <div class="caption mb-3">
                    Muestra trazas técnicas de consultas/reportes (éxito o error), útil para soporte y diagnóstico.
                  </div>
                  <v-data-table :headers="auditHeaders" :items="auditRows" dense disable-pagination hide-default-footer>
                    <template v-slot:item.success="{ item }">
                      <span :class="['pill', item.success ? 'pill--success' : 'pill--warn']">{{ item.success ? "OK" : "Error" }}</span>
                    </template>
                    <template v-slot:item.createdAt="{ item }">{{ formatDateTime(item.createdAt) }}</template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { priceListsService } from "../services/priceLists";
import { pricingStockService } from "../services/pricingStock";
import { currentAccountsService } from "../services/currentAccounts";
import { productsService } from "../services/products";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "PricingAdminDashboard",
  components: { HelpHint },
  data() {
    return {
      loading: false,
      error: "",
      priceLists: [],
      providers: [],
      filters: { priceListId: "", providerId: "", dateFrom: "", dateTo: "" },
      overview: {
        totals: {
          updates: 0,
          changes: 0,
          changedProducts: 0,
          avgDeltaPct: 0,
        },
      },
      trendRows: [],
      providerRows: [],
      topRows: [],
      auditRows: [],
      uploadFile: null,
      uploadDescription: "Carga desde dashboard admin",
      uploadLoading: false,
      uploadResult: null,
      coefficientUploadFile: null,
      coefficientUploadLoading: false,
      coefficientUploadResult: null,
      currentAccounts: [],
      ccRecentMovements: [],
      trendHeaders: [
        { text: "Período", value: "bucket" },
        { text: "Cambios", value: "changes" },
        { text: "Productos", value: "changedProducts" },
        { text: "Var % promedio", value: "avgDeltaPct" },
      ],
      topHeaders: [
        { text: "Código", value: "productCode" },
        { text: "Artículo", value: "productName" },
        { text: "Lista", value: "listCode" },
        { text: "Anterior", value: "previousPrice" },
        { text: "Nuevo", value: "newPrice" },
        { text: "Variación %", value: "deltaPct" },
        { text: "Fecha", value: "changedAt" },
      ],
      auditHeaders: [
        { text: "Fecha", value: "createdAt" },
        { text: "Acción", value: "action" },
        { text: "Recurso", value: "resourceType" },
        { text: "Estado", value: "success" },
      ],
      uploadErrorHeaders: [
        { text: "Fila", value: "row" },
        { text: "Código", value: "code" },
        { text: "Motivo", value: "reason" },
      ],
      coefficientPreviewHeaders: [
        { text: "Fila", value: "row" },
        { text: "Código", value: "code" },
        { text: "Artículo", value: "productName" },
        { text: "Coef. anterior", value: "previousCoefficient" },
        { text: "Coef. nuevo", value: "newCoefficient" },
        { text: "Estado", value: "status" },
      ],
      ccMovementHeaders: [
        { text: "Fecha", value: "createdAt" },
        { text: "Cuenta", value: "account" },
        { text: "Tipo", value: "movementType" },
        { text: "Importe", value: "amount" },
        { text: "Detalle", value: "description" },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    isAdmin() {
      const roles = (this.user && this.user.roles) || [];
      return Array.isArray(roles) && roles.includes("admin");
    },
    priceListOptions() {
      return this.priceLists.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    providerOptions() {
      return this.providers.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    canUpload() {
      return Boolean(this.filters.priceListId && this.uploadFile);
    },
    canApplyPriceUpload() {
      return Boolean(this.canUpload && this.uploadResult && this.uploadResult.preview && this.uploadResult.fileHash);
    },
    canApplyCoefficientUpload() {
      return Boolean(this.coefficientUploadFile && this.coefficientUploadResult && this.coefficientUploadResult.preview && this.coefficientUploadResult.fileHash);
    },
    maxTrendChanges() {
      if (!this.trendRows.length) return 1;
      return Math.max(...this.trendRows.map((x) => Number(x.changes || 0)), 1);
    },
    trendChangesSeries() {
      return this.trendRows.map((x) => Number(x.changes || 0));
    },
    trendAvgPctSeries() {
      return this.trendRows.map((x) => Number(x.avgDeltaPct || 0));
    },
    lastTrendChanges() {
      if (!this.trendRows.length) return 0;
      return Number(this.trendRows[this.trendRows.length - 1].changes || 0);
    },
    lastTrendAvgPct() {
      if (!this.trendRows.length) return 0;
      return Number(this.trendRows[this.trendRows.length - 1].avgDeltaPct || 0);
    },
    trendAvgPctColor() {
      return this.lastTrendAvgPct >= 0 ? "#16a34a" : "#dc2626";
    },
    balanceLabel() {
      const inc = Number(this.overview.totals.increases || 0);
      const dec = Number(this.overview.totals.decreases || 0);
      if (inc > dec) return "Predominan aumentos";
      if (dec > inc) return "Predominan bajas";
      return "Comportamiento equilibrado";
    },
    quickSummaryLine1() {
      return `Se impactaron ${this.overview.totals.changedProducts || 0} artículos en ${this.overview.totals.changes || 0} cambios.`;
    },
    quickSummaryLine2() {
      return `Variación promedio del período: ${this.formatSignedPct(this.overview.totals.avgDeltaPct)}.`;
    },
    changesUpPct() {
      const inc = Number(this.overview.totals.increases || 0);
      const dec = Number(this.overview.totals.decreases || 0);
      const total = inc + dec;
      if (!total) return 0;
      return Number(((inc * 100) / total).toFixed(1));
    },
    changesDownPct() {
      const inc = Number(this.overview.totals.increases || 0);
      const dec = Number(this.overview.totals.decreases || 0);
      const total = inc + dec;
      if (!total) return 0;
      return Number(((dec * 100) / total).toFixed(1));
    },
    changesDonutStyle() {
      const up = this.changesUpPct;
      return {
        background: `conic-gradient(#16a34a 0 ${up}%, #dc2626 ${up}% 100%)`
      };
    },
    providerTopSlices() {
      const palette = ["#0284c7", "#0ea5e9", "#38bdf8", "#7dd3fc", "#bae6fd"];
      return this.providerRows.slice(0, 5).map((row, idx) => ({
        ...row,
        color: palette[idx] || "#cbd5e1"
      }));
    },
    providerShareDonutStyle() {
      const slices = this.providerTopSlices;
      if (!slices.length) return { background: "conic-gradient(#e2e8f0 0 100%)" };
      let cursor = 0;
      const parts = slices.map((item) => {
        const from = cursor;
        const to = Math.min(100, cursor + Number(item.sharePct || 0));
        cursor = to;
        return `${item.color} ${from}% ${to}%`;
      });
      if (cursor < 100) parts.push(`#e2e8f0 ${cursor}% 100%`);
      return { background: `conic-gradient(${parts.join(", ")})` };
    },
    ccDebtCount() {
      return this.currentAccounts.filter((x) => Number(x.balance || 0) > 0).length;
    },
    ccCreditCount() {
      return this.currentAccounts.filter((x) => Number(x.balance || 0) < 0).length;
    },
    ccZeroCount() {
      return this.currentAccounts.filter((x) => Number(x.balance || 0) === 0).length;
    },
    ccTotalCount() {
      return this.currentAccounts.length || 1;
    },
    ccDebtPct() {
      return Number(((this.ccDebtCount * 100) / this.ccTotalCount).toFixed(1));
    },
    ccCreditPct() {
      return Number(((this.ccCreditCount * 100) / this.ccTotalCount).toFixed(1));
    },
    ccZeroPct() {
      const remaining = 100 - this.ccDebtPct - this.ccCreditPct;
      return Number(Math.max(0, remaining).toFixed(1));
    },
    ccDonutStyle() {
      const a = this.ccDebtPct;
      const b = this.ccDebtPct + this.ccCreditPct;
      return {
        background: `conic-gradient(#dc2626 0 ${a}%, #16a34a ${a}% ${b}%, #94a3b8 ${b}% 100%)`,
      };
    },
    ccTotalDebt() {
      return this.currentAccounts.reduce((acc, x) => {
        const b = Number(x.balance || 0);
        return b > 0 ? acc + b : acc;
      }, 0);
    },
    ccTotalCredit() {
      return this.currentAccounts.reduce((acc, x) => {
        const b = Number(x.balance || 0);
        return b < 0 ? acc + Math.abs(b) : acc;
      }, 0);
    },
  },
  created() {
    this.refreshAll();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      return parseApiError(error, "No pudimos completar la operación.");
    },
    buildParams() {
      const params = {};
      if (this.filters.priceListId) params.priceListId = this.filters.priceListId;
      if (this.filters.providerId) params.providerId = this.filters.providerId;
      if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom;
      if (this.filters.dateTo) params.dateTo = this.filters.dateTo;
      return params;
    },
    async refreshAll() {
      if (!this.isAdmin) {
        this.error = "Acceso solo para administradores.";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const [listsRes, providersRes] = await Promise.all([
          priceListsService.list(),
          pricingStockService.getProviders({ isActive: true }),
        ]);
        this.priceLists = this.unwrap(listsRes) || [];
        this.providers = this.unwrap(providersRes) || [];

        if (!this.filters.priceListId && this.priceLists.length) this.filters.priceListId = this.priceLists[0].id;

        const params = this.buildParams();
        const [overviewRes, trendRes, providerRes, topRes, auditRes, currentAccountsRes] = await Promise.all([
          pricingStockService.getReportOverview(params),
          pricingStockService.getReportTrend({ ...params, groupBy: "day" }),
          pricingStockService.getReportByProvider(params),
          pricingStockService.getReportTopChanges({ ...params, limit: 30 }),
          pricingStockService.getAuditLogs({ resourceType: "pricing_report", limit: 12 }),
          currentAccountsService.list({ isActive: true }),
        ]);

        const overviewData = this.unwrap(overviewRes) || {};
        const trendData = this.unwrap(trendRes) || {};
        const providerData = this.unwrap(providerRes) || {};
        const topData = this.unwrap(topRes) || {};
        this.overview = overviewData;
        this.trendRows = Array.isArray(trendData.rows) ? trendData.rows : [];
        this.providerRows = Array.isArray(providerData.rows) ? providerData.rows : [];
        this.topRows = Array.isArray(topData.rows) ? topData.rows : [];
        this.auditRows = this.unwrap(auditRes) || [];
        this.currentAccounts = this.unwrap(currentAccountsRes) || [];
        await this.fetchRecentCurrentAccountMovements();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchRecentCurrentAccountMovements() {
      this.ccRecentMovements = [];
      const ranked = [...this.currentAccounts]
        .sort((a, b) => Math.abs(Number(b.balance || 0)) - Math.abs(Number(a.balance || 0)))
        .slice(0, 8);
      if (!ranked.length) return;
      const results = await Promise.all(
        ranked.map((acc) =>
          currentAccountsService
            .listMovements(acc.id, { limit: 5 })
            .then((res) => ({ acc, rows: this.unwrap(res) || [] }))
            .catch(() => ({ acc, rows: [] }))
        )
      );
      const merged = [];
      results.forEach(({ acc, rows }) => {
        rows.forEach((row) => {
          merged.push({
            ...row,
            accountId: acc.id,
            accountCode: acc.code,
            accountName: acc.name,
          });
        });
      });
      merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      this.ccRecentMovements = merged.slice(0, 15);
    },
    async runUpload(preview) {
      if (!this.canUpload) return;
      this.uploadLoading = true;
      this.error = "";
      try {
        const formData = new FormData();
        formData.append("file", this.uploadFile);
        formData.append("description", this.uploadDescription || "Carga desde dashboard admin");
        if (!preview) formData.append("previewHash", this.uploadResult.fileHash);
        const response = await priceListsService.uploadItemsFile(
          this.filters.priceListId,
          formData,
          { preview, skipEmptyPrices: true }
        );
        this.uploadResult = this.unwrap(response);
        if (!preview) await this.refreshAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.uploadLoading = false;
      }
    },
    async runCoefficientUpload(preview) {
      if (!this.coefficientUploadFile) return;
      this.coefficientUploadLoading = true;
      this.error = "";
      try {
        const formData = new FormData();
        formData.append("file", this.coefficientUploadFile);
        if (!preview) formData.append("previewHash", this.coefficientUploadResult.fileHash);
        const response = await productsService.importUtilityCoefficients(formData, { preview });
        this.coefficientUploadResult = this.unwrap(response);
        if (!preview) await this.refreshAll();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.coefficientUploadLoading = false;
      }
    },
    formatNumber(value) {
      const n = Number(value || 0);
      return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    formatSignedPct(value) {
      const n = Number(value || 0);
      const signed = n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
      return `${signed}%`;
    },
    formatDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("es-AR");
    },
  },
};
</script>

<style scoped>
.page { padding: 0; }
.v-data-table :deep(tbody td),
.v-data-table :deep(thead th) {
  height: 32px !important;
  padding: 2px 8px !important;
  font-weight: 500;
}
.page-hero {
  background: var(--bg-card);
  border-radius: 18px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-md);
}
.page-hero h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; color: var(--text-primary); }
.page-hero p { margin: 0; color: var(--text-secondary); }
.page-card {
  border-radius: 18px;
  border: 1px solid var(--border-subtle);
  background: var(--bg-card);
  box-shadow: var(--shadow-md);
}
.actions-row { display: flex; gap: 10px; flex-wrap: wrap; }
.kpi-card { padding: 14px; border-radius: 14px; }
.kpi-label { color: #64748b; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; }
.kpi-value { font-size: 28px; font-weight: 700; margin-top: 6px; }
.insight-card { padding: 14px; border-radius: 14px; min-height: 110px; }
.insight-title { font-size: 13px; font-weight: 700; margin-bottom: 8px; color: #1f2937; }
.insight-row { display: flex; gap: 8px; flex-wrap: wrap; }
.donut-wrap { display: flex; align-items: center; gap: 12px; }
.donut { width: 74px; height: 74px; border-radius: 999px; position: relative; flex: 0 0 auto; }
.donut::after { content: ""; position: absolute; inset: 14px; border-radius: 999px; background: #fff; }
.donut-legend { display: grid; gap: 4px; }
.legend-item { font-size: 12px; color: #334155; display: flex; align-items: center; gap: 6px; }
.legend-dot { width: 9px; height: 9px; border-radius: 999px; display: inline-block; }
.legend-dot--up { background: #16a34a; }
.legend-dot--down { background: #dc2626; }
.trend-row { margin-bottom: 8px; }
.trend-head { display: flex; justify-content: space-between; gap: 10px; margin-bottom: 4px; font-size: 12px; }
.trend-track { height: 8px; border-radius: 999px; background: #e5e7eb; overflow: hidden; margin-bottom: 4px; }
.trend-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #2563eb, #38bdf8); }
.chart-title { font-size: 12px; font-weight: 700; color: #334155; margin-bottom: 4px; }
.chart-meta { font-size: 12px; color: #64748b; margin-top: 4px; }
.provider-share-row { margin-bottom: 14px; }
.provider-share-head { display: flex; justify-content: space-between; gap: 10px; font-weight: 600; margin-bottom: 6px; }
.provider-share-track { height: 10px; border-radius: 999px; background: #e2e8f0; overflow: hidden; margin-bottom: 4px; }
.provider-share-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #0284c7, #0ea5e9); }
.pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.pill--success { color: #0f766e; background: #ccfbf1; }
.pill--warn { color: #9a3412; background: #ffedd5; }

html[data-theme="dark"] .kpi-label,
html[data-theme="dark"] .chart-meta,
html[data-theme="dark"] .legend-item,
html[data-theme="dark"] .trend-head,
html[data-theme="dark"] .chart-title {
  color: #a8bbda;
}

html[data-theme="dark"] .insight-title,
html[data-theme="dark"] .kpi-value,
html[data-theme="dark"] .provider-share-head {
  color: #e5edff;
}

html[data-theme="dark"] .donut::after {
  background: #121f39;
}

html[data-theme="dark"] .trend-track,
html[data-theme="dark"] .provider-share-track {
  background: #2a3b5c;
}

html[data-theme="dark"] .pill--success {
  color: #86efac;
  background: rgba(22, 163, 74, 0.2);
}

html[data-theme="dark"] .pill--warn {
  color: #fdba74;
  background: rgba(234, 88, 12, 0.22);
}
@media (max-width: 960px) { .page-hero { flex-direction: column; align-items: flex-start; } }
</style>
